<template>
    <div
        :class="{
            disabled,
            loading: skeleton,
            checked: checked && !indeterminate,
            indeterminate: (inverse ? value : !value) && indeterminate,
            left
        }"
        class="checkbox"
        @click.stop="toggle"
    >
        <input
            v-if="!skeleton"
            :checked="checked"
            :indeterminate.prop="(inverse ? value : !value) && indeterminate"
            :disabled="disabled"
            type="checkbox"
        />
        <label
            :class="{ checked, solid, skeleton, touch: $browser.is.touch }"
            v-bind="$attrs"
        >
            <div class="checkbox-wrapper">
                <Icon
                    :name="solid && checked ? 'checkbox-checked-solid' : 'checkbox-frame'"
                    class="checkbox-frame"
                />
                <Transition name="fadefast">
                    <Icon
                        v-if="indeterminate"
                        name="checkbox-indeterminate"
                        class="checkbox-marker"
                    />
                    <Icon
                        v-else-if="!(solid && checked)"
                        name="checkbox-tick"
                        class="checkbox-marker"
                    />
                </Transition>
            </div>
            <div
                v-if="$slots.default"
                class="slot-wrapper"
            >
                <slot />
            </div>
        </label>
    </div>
</template>

<script>
import Icon from '@/components/utility/icon';

export default {
    name: 'Checkbox',
    components: { Icon },
    props: {
        value: { type: Boolean, default: false },
        indeterminate: { type: Boolean, default: false },
        inverse: { type: Boolean, default: false },
        skeleton: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        left: { type: Boolean, default: false },
        solid: { type: Boolean, default: false }
    },
    emits: ['click', 'update:value'],
    slots: ['default'],
    computed: {
        checked() {
            return this.inverse ? !(this.value === true) : this.value === true;
        }
    },
    methods: {
        toggle(event) {
            if (this.disabled) return;

            this.$emit('click', event);
            this.$emit('update:value', !this.value);
        }
    }
};
</script>

<style lang="less" scoped>
.checkbox {
    --checkbox-color: var(--primary);
    --checkbox-hover-color: var(--primary-fade-2);

    cursor: pointer;
    display: inline-flex;
    align-items: center;
    position: relative;
    color: var(--coal);
    min-height: 24px;
    min-width: 24px;
    background: 0;

    input {
        position: absolute;
        visibility: hidden;
        margin: 0;
    }
    label {
        flex: 1 1 auto;
        position: relative;
        display: flex;
        align-items: center;

        margin: 0;
        cursor: pointer;
        min-width: 24px;
        min-height: 24px;
        font-weight: 400;
        .slot-wrapper {
            flex: 1 1 auto;
            text-align: left;
            margin-left: 8px;
            margin-right: 24px;
            line-height: 16px;
            display: flex;
            align-items: center;
        }
        .checkbox-wrapper {
            position: relative;
            display: flex;
            .checkbox-frame {
                fill: var(--anchor);
                color: var(--anchor);
            }
            .checkbox-marker {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;

                opacity: 0;
                fill: var(--anchor);
                color: var(--anchor);
            }
        }
        &.skeleton .checkbox-wrapper  .checkbox-frame { opacity: 0; }
        &.checked:not(.skeleton) .checkbox-wrapper .checkbox-marker { opacity: 1; }
    }
    &.left {
        flex-direction: row-reverse;
        label {
            flex-direction: row-reverse;
            .slot-wrapper {
                margin-left: 0;
                margin-right: 8px;
            }
        }
    }
    &.disabled label {
        color: var(--steel);
        cursor: default;
        .checkbox-frame, .checkbox-marker {
            fill: var(--steel);
            color: var(--steel);
        }
    }

    &:not(.disabled) {
        label:not(.skeleton) {
            &.checked {
                .checkbox-frame, .checkbox-marker {
                    fill: var(--checkbox-color);
                    color: var(--checkbox-color);
                }
                &:hover:not(.touch) .checkbox-marker {
                    fill: var(--checkbox-hover-color);
                    color: var(--checkbox-hover-color);
                }
                &.solid {
                    .checkbox-marker {
                        fill: #FFF;
                        color: #FFF;
                    }
                }
            }
            &:hover:not(.touch) .checkbox-marker {
                opacity: 1;
                fill: var(--steel);
                color: var(--steel);
            }
        }
    }
}
</style>

<style lang="less">
.checkbox label {
    .checkbox-frame .bg {
        fill: transparent;
    }
}
</style>
